/**
 * The notation devices: status chips over the whole vocabulary, the profitability bar,
 * completion meters, and the running-timer indicator.
 *
 * Every one of these carries its own text alternative, because each encodes meaning in
 * position or colour. The Ledger law that matters here is the seventh: any non-obvious
 * notation ships with an inline legend, which is what lets a dense ledger teach itself.
 */
import type { ReactNode } from "react";
import {
  AuthorityReportStatus,
  EngagementLevel,
  InvoiceStatus,
  MonthlyReportingStatus,
  ProfitabilityBand,
  ScanStatus,
  SignatureStatus,
  TaskStatus,
  WorkspaceStatus,
  type StatusMeta,
} from "@onecpa/shared";
import { Badge } from "./Badge.js";
import { Money } from "./Money.js";

/**
 * Every status vocabulary in the product, in one lookup.
 *
 * A screen names the vocabulary and the key; it never picks a label or a colour. That
 * is the whole point — the legacy stored Hebrew status strings, so a typo produced a
 * new status nobody could filter on, and every screen chose its own shade.
 */
const VOCABULARIES = {
  monthly: MonthlyReportingStatus,
  authorityReport: AuthorityReportStatus,
  workspace: WorkspaceStatus,
  task: TaskStatus,
  invoice: InvoiceStatus,
  profitability: ProfitabilityBand,
  engagement: EngagementLevel,
  signature: SignatureStatus,
  scan: ScanStatus,
} as const satisfies Record<string, Record<string, StatusMeta>>;

export type Vocabulary = keyof typeof VOCABULARIES;

export interface StatusBadgeProps {
  vocabulary: Vocabulary;
  status: string;
  className?: string;
}

export function StatusBadge({ vocabulary, status, className = "" }: StatusBadgeProps) {
  const meta = (VOCABULARIES[vocabulary] as Record<string, StatusMeta | undefined>)[status];

  // An unknown key is a data problem, and showing it raw is how it gets noticed. The
  // alternative — a neutral chip reading nothing — hides it.
  if (!meta) {
    return (
      <Badge tone="neutral" className={className}>
        {status}
      </Badge>
    );
  }

  return (
    <Badge tone={meta.tone} className={className}>
      {meta.label}
    </Badge>
  );
}

export function statusLabel(vocabulary: Vocabulary, status: string): string {
  return (
    (VOCABULARIES[vocabulary] as Record<string, StatusMeta | undefined>)[status]?.label ?? status
  );
}

/**
 * The profitability bar, reading out from a centred zero baseline.
 *
 * `dir="ltr"` is on the bar track only, and this is the legitimate case for it: a chart
 * axis is not text, and losses must extend consistently to one side. The design guide
 * warns that forcing direction on anything else silently reversed strings like
 * `₪1,200 ומעלה`.
 */
export function ProfitabilityBar({
  marginAgorot,
  maxAgorot,
  label,
}: {
  marginAgorot: number;
  maxAgorot: number;
  label?: string;
}) {
  const scale = Math.max(maxAgorot, 1);
  const magnitude = Math.min(Math.abs(marginAgorot) / scale, 1);
  const width = `${(magnitude * 50).toFixed(1)}%`;
  const isLoss = marginAgorot < 0;

  return (
    <span className="flex items-center gap-2">
      <span className="relative h-2.5 min-w-24 flex-1 rounded-pill bg-wash" dir="ltr">
        {/* The zero baseline, so a short bar is still readable as a direction. */}
        <span className="absolute inset-y-0 left-1/2 w-px bg-rule-strong" aria-hidden="true" />
        <span
          className={`absolute inset-y-0 rounded-pill ${isLoss ? "bg-red-ink" : "bg-data-1"}`}
          style={isLoss ? { right: "50%", width } : { left: "50%", width }}
          aria-hidden="true"
        />
      </span>
      <Money agorot={marginAgorot} className="w-20 text-end" />
      {label && <span className="sr-only">{label}</span>}
    </span>
  );
}

/**
 * A completion meter for checklist cases.
 *
 * Shows the fraction as text beside the bar, because "63%" alone does not tell a
 * bookkeeper whether that is five of eight items or fifty of eighty.
 */
export function CompletionMeter({
  closed,
  total,
  blocking = 0,
}: {
  closed: number;
  total: number;
  blocking?: number;
}) {
  const percent = total === 0 ? 0 : Math.round((closed / total) * 100);
  // Ochre still means blocked-and-late. Complete versus in-progress is a
  // category distinction, so it reads off the data ramp.
  const tone = blocking > 0 ? "bg-ochre" : percent === 100 ? "bg-data-1" : "bg-data-3";

  return (
    <span className="flex items-center gap-2">
      <span
        className="h-1.5 w-20 overflow-hidden rounded-pill bg-wash"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${closed} מתוך ${total} פריטים`}
      >
        <span className={`block h-full ${tone}`} style={{ width: `${percent}%` }} />
      </span>
      <span className="tnum text-2xs text-ink-faint">
        {closed}/{total}
      </span>
    </span>
  );
}

/**
 * The running-timer indicator.
 *
 * Ticks in the header while a timer runs. The dot pulses because a timer that looks
 * static is a timer someone forgets is running — which is how an eight-hour entry
 * happens.
 */
export function TimerIndicator({
  elapsedMinutes,
  clientName,
  onStop,
}: {
  elapsedMinutes: number;
  clientName?: string | null;
  onStop?: () => void;
}) {
  const hours = Math.floor(elapsedMinutes / 60);
  const minutes = elapsedMinutes % 60;

  return (
    <span className="inline-flex h-6 items-center gap-2 rounded-pill border border-rule bg-paper px-2.5 text-xs leading-none">
      <span className="size-1.5 animate-pulse rounded-full bg-ledger-bright" aria-hidden="true" />
      {/* bdi, not dir=ltr: the landing's own comment records that forcing direction
          reversed strings like "רונית · 00:42:13". */}
      <bdi className="tnum text-ink">
        {String(hours).padStart(2, "0")}:{String(minutes).padStart(2, "0")}
      </bdi>
      {clientName && <span className="max-w-28 truncate text-ink-muted">{clientName}</span>}
      {onStop && (
        <button
          type="button"
          onClick={onStop}
          className="font-semibold text-ledger hover:underline"
        >
          עצור
        </button>
      )}
    </span>
  );
}

/** A labelled fact, for identity headers and detail panels. */
export function Fact({ label, children }: { label: string; children?: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs text-ink-faint">{label}</dt>
      <dd className="truncate text-sm text-ink">{children ?? "—"}</dd>
    </div>
  );
}

/** A hairline-bounded panel. The app's only container treatment. */
export function Panel({
  title,
  action,
  children,
  className = "",
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`ruled ${className}`}>
      {title && (
        <div className="flex items-center gap-3 border-b border-rule px-3.5 py-2.5">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {action && <div className="ms-auto">{action}</div>}
        </div>
      )}
      {children}
    </section>
  );
}
